<template>
    <section class="container-fluid">
        <h1 v-html="$t('trade_loops')"></h1>

        <b-form-group :label="$t('which_tier_to_generate_loop')">
            <b-radio-group v-model="minTier" :options="tierOptions" buttons button-variant="outline-secondary" size="sm"></b-radio-group>
        </b-form-group>

        <h2>{{ $t('generated_loops') }}</h2>

        <div :class="$style.loopGrid">
            <div class="mb-3">
                <p :class="$style['icon-loop']">
                    <font-awesome-icon icon="microchip" :style="{ color: getEcoColor('high_tech') }"></font-awesome-icon>
                    <font-awesome-icon icon="long-arrow-alt-right"></font-awesome-icon>
                    <font-awesome-icon icon="car-battery" :style="{ color: getEcoColor('power_generation') }"></font-awesome-icon>
                    <font-awesome-icon icon="long-arrow-alt-right"></font-awesome-icon>
                    <font-awesome-icon icon="hammer" :style="{ color: getEcoColor('mining') }"></font-awesome-icon>
                    <font-awesome-icon icon="long-arrow-alt-right"></font-awesome-icon>
                    <font-awesome-icon icon="cogs" :style="{ color: getEcoColor('manufacturing') }"></font-awesome-icon>
                </p>

                <p v-if="!loops.large || !loops.large.length" class="info-box">No loops could be generated.</p>
                <template v-else v-for="(loop, index) in loops.large">
                    <h3 :key="`large-heading-${index}`">Loop {{ index + 1 }}</h3>
                    <trade-loops :loop="loop" :key="`large-list-${index}`"></trade-loops>
                </template>
            </div>

            <div>
                <p :class="$style['icon-loop']">
                    <font-awesome-icon icon="dolly" :style="{ color: getEcoColor('trading') }"></font-awesome-icon>
                    <font-awesome-icon icon="long-arrow-alt-right"></font-awesome-icon>
                    <font-awesome-icon icon="dna" :style="{ color: getEcoColor('alchemical') }"></font-awesome-icon>
                    <font-awesome-icon icon="long-arrow-alt-right"></font-awesome-icon>
                    <font-awesome-icon icon="flask" :style="{ color: getEcoColor('scientific') }"></font-awesome-icon>
                </p>

                <p v-if="!loops.small || !loops.small.length" class="info-box">No loops could be generated.</p>
                <template v-else v-for="(loop, index) in loops.small">
                    <h3 :key="`small-heading-${index}`">Loop {{ index + 1 }}</h3>
                    <trade-loops :loop="loop" :key="`small-list-${index}`"></trade-loops>
                </template>
            </div>
        </div>

    </section>
</template>

<script>
import bFormGroup from 'bootstrap-vue/es/components/form-group/form-group';
import bRadioGroup from 'bootstrap-vue/es/components/form-radio/form-radio-group';
import TradeLoops from '@/components/TradeLoops';
import SystemService from '@/services/systemService';
import _collection from 'lodash/collection';
import utils from '@/mixins/utils';
import { mapGetters, mapActions } from 'vuex';
export default {
    data () {
        return {
            systems: undefined,
            minTier: 3
        };
    },
    components: {
        bFormGroup,
        bRadioGroup,
        TradeLoops
    },
    mixins: [utils],
    computed: {
        tierOptions () {
            return [
                { value: 1, text: this.$t('all_tiers') },
                { value: 2, text: this.$t('tier_n_and_up', [2]) },
                { value: 3, text: this.$t('only_tier_n', [3]) }
            ];
        },
        economies () {
            return {
                types: this.$root.shared.economyTypes,
                levels: this.$root.shared.economyLevels
            };
        },
        reducedSystems () {
            return this.systems.filter(s => s.tier >= this.minTier);
            // return this.getSystems.filter(s => s.tier >= this.minTier);
        },
        ecoGroups () {
            return _collection.groupBy(this.reducedSystems, function(s) { return s.ecoType; });
        },
        loops () {
            if (!this.systems) return false;
            let loopsLarge = [];
            let loopsSmall = [];

            const typesBig = ['high_tech', 'power_generation', 'mining', 'manufacturing'];
            const typesSmall = ['trading', 'advanced_materials', 'scientific'];

            const countLarge = typesBig.map(t => this.ecoGroups[t] ? this.ecoGroups[t].length : 0);
            const countSmall = typesSmall.map(t => this.ecoGroups[t] ? this.ecoGroups[t].length : 0);

            const minLarge = Math.min(...countLarge);
            const minSmall = Math.min(...countSmall);

            for (let i = 0; i < minLarge; i++) {
                typesBig.forEach(t => {
                    loopsLarge[i] = loopsLarge[i] || [];
                    loopsLarge[i].push(this.ecoGroups[t][i]);
                });
            }

            for (let i = 0; i < minSmall; i++) {
                typesSmall.forEach(t => {
                    loopsSmall[i] = loopsSmall[i] || [];
                    loopsSmall[i].push(this.ecoGroups[t][i]);
                });
            }

            return { large: loopsLarge, small: loopsSmall };
        },
        ...mapGetters([
            'getSystems'
        ])
    },
    methods: {
        bundleLoop () {

        },
        async getEcoSystems () {
            this.systems = (await SystemService.getEconomyGroups({ userHash: this.$ls.get('userHash'), groupBy: ['ecoType', 'id'] })).data.systems;
        },
        genericLoops (size) {
            if (!this.getSystems) return false;

            const typesBig = ['high_tech', 'power_generation', 'mining', 'manufacturing'];
            const typesSmall = ['trading', 'advanced_materials', 'scientific'];
            const types = size === 'big' ? typesBig : typesSmall;

            const systems = this.reducedSystems.filter(s => types.includes(this.getEcoCategory(s.type)));

            let grouped = {};
            let counts = {};
            systems.forEach(s => {
                const cat = this.getEcoCategory(s.type);
                if (!grouped[cat]) grouped[cat] = [];
                if (!counts[cat]) counts[cat] = 0;
                grouped[cat].unshift(s);
                counts[cat] = counts[cat] + 1;
            });
            const aCounts = Object.keys(counts).map(key => counts[key]);

            if (aCounts.length !== types.length) return;

            const loopsCount = Math.min(...aCounts);
            let loop = [];

            for (let i = 0; i < loopsCount; i++) {
                types.forEach(t => {
                    loop.push(grouped[t][i]);
                });
            }

            // console.log(aCounts.length, types.length, counts, grouped, loop);

            return loop;
        },
        ...mapActions([
            'GET_API_DATA'
        ])
    },
    mounted () {
        this.getEcoSystems();
        this.GET_API_DATA();
    }
};
</script>

<style module scoped lang="scss">
    pre {
        background-color: white;
    }

    .loopGrid {
        @media(min-width: 54em) {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-gap: 1em;
        }
    }

    .icon-loop {
        font-size: 1.5em;
    }

    .icon-loop > * {
        margin-right: 0.5em;
    }

    .loop th,
    .loop td {
        padding: 0 0.5em;
        text-align: end;
    }

    .loop th:nth-child(1),
    .loop td:nth-child(1) {
        min-width: 1em;
    }

    .loop th:nth-child(3),
    .loop td:nth-child(3) {
        text-align: start;
    }
</style>
